import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Heading, Box, Spacing, Button, ActivityIndicator } from '../ui';
import { useAuth } from '../hooks';
import { Page, Main, Logo, LayoutBox } from '../components';
import { AUTHORIZATION_LOCAL_STORAGE_KEY, AUTH_PROVIDER } from '../constants';

// The hidden authorization-iframe below is a Kalidasa-specific mechanism (not
// part of OIDC): it loads a Kalidasa page that mints a token from the shared
// Kalidasa session cookie and posts it back over postMessage. The auth portal
// has no such endpoint, so in portal mode the iframe is not rendered and the
// login button shows immediately — pressing it runs the standard redirect,
// which returns without a prompt when a portal SSO session already exists.
const useKalidasaIframe = AUTH_PROVIDER === 'kalidasa';

const LoginPage = React.forwardRef((props, ref) => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(useKalidasaIframe);
  const [cancelIframeAuth, setcancelIframeAUth] = useState(false);
  const authIframe = useRef(null);

  // Wait 5 seconds max for auto-auth, if not allow user to login manually
  setTimeout(() => {
    setcancelIframeAUth(true);
    setLoading(false);
  }, 5000);

  const sendMessage = (iframeRef, message) => {
    iframeRef.current.contentWindow.postMessage(message, `${import.meta.env.VITE_KALIDASA_BASE_URI}`);
  };

  const receiveMessage = useCallback(
    (message) => {
      if (!cancelIframeAuth && message.origin === import.meta.env.VITE_KALIDASA_BASE_URI.replace(/\/$/, '') && message.data.access_token) {
        window.localStorage.setItem(AUTHORIZATION_LOCAL_STORAGE_KEY, JSON.stringify(message.data));
        window.location.reload();
        return;
      }

      setLoading(false);
    },
    [cancelIframeAuth]
  );

  const iframeLoaded = () => {
    sendMessage(authIframe, { type: 'request_token', clientId: import.meta.env.VITE_OPENQUOTE_CLIENT_ID });
  };

  useEffect(() => {
    if (!useKalidasaIframe) return;
    window.addEventListener('message', receiveMessage);
    return () => window.removeEventListener('message', receiveMessage);
  }, [receiveMessage]);

  return (
    <Page {...props} ref={ref}>
      <Main pb="$13" layout="center">
        <Box justifyContent="center" alignItems="center">
          <LayoutBox layout="bottom-center" row>
            <Logo width={90} height={80} mr="$-1" />
            <Heading pb="$1.5" level={3} weight="$regular" letterSpacing={1} uppercase>
              Understory
            </Heading>
          </LayoutBox>
          <Spacing vertical={8} />
          {loading === true && !cancelIframeAuth ? (
            <ActivityIndicator />
          ) : (
            <Button width="150" large onPress={login}>
              Login
            </Button>
          )}
          {useKalidasaIframe && (
            <div style={{ display: 'none' }}>
              <iframe
                title="understoryWeather"
                src={`${import.meta.env.VITE_KALIDASA_BASE_URI}/authorization-iframe`}
                ref={authIframe}
                onLoad={iframeLoaded}
              />
            </div>
          )}
        </Box>
      </Main>
    </Page>
  );
});

export { LoginPage };
